"use client"

import { CloseOutlined, DeleteOutlined, EditOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons"
import type { TableColumnsType, TableProps } from "antd"
import { Button, Image, message, Modal, Space, Table } from "antd"
import Title from "antd/es/typography/Title"
import { useRouter } from "next/navigation"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useForm, useWatch } from "react-hook-form"
import { ADMIN_API_CONFIG } from "../../../../constants/api-configs"
import { t } from "../../../../languages/i18n"
import { FileUpdate, Formats, FormValuesMedia, MediaData } from "../../../../lib/config/types"
import { formatDate } from "../../../../lib/utils"
import { deleteMedia, fetchMedia, updateMedia } from "../services/apiServices"
import MediaModalBodyEdit from "./MediaModalEditBody"

/* ---------------------------------- Hooks --------------------------------- */
/** Phân trang: mỗi lần đổi trang -> GHI ĐÈ dữ liệu trang */
function usePagedMedia(searchTerm: string) {
  const [data, setData] = useState<MediaData[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 })

  const fetchPage = useCallback(
    async (pg = pagination) => {
      setLoading(true)
      try {
        const resp = await fetchMedia(pg.current, pg.pageSize, searchTerm)
        const list: MediaData[] = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : []
        const totalFromApi =
          resp?.meta?.pagination?.total ??
          resp?.pagination?.total ??
          resp?.total ??
          (Array.isArray(list) ? list.length : 0)

        setData(list) // GHI ĐÈ
        setPagination({ ...pg, total: totalFromApi })
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    },
    [pagination, searchTerm],
  )

  // Đổi từ khoá -> reset về trang 1
  useEffect(() => {
    const first = { current: 1, pageSize: 50, total: 0 }
    setPagination(first)
    fetchPage(first)
  }, [searchTerm]) // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, pagination, fetchPage, setPagination }
}

/** See-more: tải dần, CỘNG DỒN & unique theo id */
function useInfiniteMedia(searchTerm: string) {
  const [data, setData] = useState<MediaData[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const pageSize = 50
  const [total, setTotal] = useState(0)

  const loadMore = useCallback(
    async (nextPage = page) => {
      setLoading(true)
      try {
        const resp = await fetchMedia(nextPage, pageSize, searchTerm)
        const list: MediaData[] = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : []
        const totalFromApi = resp?.meta?.pagination?.total ?? resp?.pagination?.total ?? resp?.total ?? total

        setData(prev => [...prev, ...list].filter((item, idx, self) => idx === self.findIndex(t => t.id === item.id)))
        setTotal(totalFromApi)
        setPage(nextPage)
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    },
    [page, pageSize, searchTerm, total],
  )

  // Đổi từ khoá -> reset & nạp lại
  useEffect(() => {
    setData([])
    setPage(1)
    setTotal(0)
    loadMore(1)
  }, [searchTerm]) // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, page, pageSize, total, loadMore }
}

/* ------------------------------ Component main ----------------------------- */
interface MediaListProps {
  searchTerm?: string
  isView?: boolean // true: Table phân trang | false: Grid see-more
  isOpenModal?: boolean
  onSelectMedia?: (media: MediaData | MediaData[]) => void
  selectMode?: "single" | "multi"
  onRefresh?: () => void
}

const MediaList: React.FC<MediaListProps> = ({
  searchTerm = "",
  isView = true,
  isOpenModal,
  onSelectMedia,
  selectMode = "single",
}) => {
  // form nhỏ để tái sử dụng 1-2 field phụ (không giữ pagination chung nữa)
  const { control, setValue } = useForm<FormValuesMedia>()
  const dataItemForm = useWatch({ control, name: "dataItem" }) // chủ yếu để reuse cho modal submit

  const router = useRouter()
  const [selectedMedia, setSelectedMedia] = useState<null | number>(null)
  const [selectedMediaMulti, setSelectedMediaMulti] = useState<MediaData[]>([])

  // 2 mode có state tách riêng
  const table = usePagedMedia(searchTerm)
  const grid = useInfiniteMedia(searchTerm)

  // data hiện tại theo mode
  const dataItem: MediaData[] = isView ? table.data : grid.data
  const loading: boolean = isView ? table.loading : grid.loading

  // columns
  const columns: TableColumnsType<MediaData> = useMemo(
    () => [
      {
        title: t("Thumbnail"),
        dataIndex: ["formats"],
        key: "url",
        width: 60,
        render: (formats: Formats, record: MediaData) =>
          record.mime && record.mime.startsWith("video/") ? (
            <video
              src={`${ADMIN_API_CONFIG.API_URL}${record.url}`}
              height={40}
              width={40}
              style={{ objectFit: "contain" }}
              controls={false}
              autoPlay={false}
              muted
              className="object-contain"
            />
          ) : (
            <Image
              src={`${ADMIN_API_CONFIG.API_URL}${formats?.thumbnail?.url || record.url}`}
              height={40}
              width={40}
              className="object-contain"
              preview={false}
            />
          ),
      },
      {
        title: t("Name"),
        dataIndex: ["name"],
        key: "name",
        width: 100,
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
      {
        title: t("Published"),
        dataIndex: ["createdAt"],
        key: "createdAt",
        width: 100,
        render: (createdAt: string) => {
          const d = formatDate(createdAt)
          return (
            <span className="font-normal">
              <span className="text-black-600">{d.formattedTime}</span> {d.formattedDate}
            </span>
          )
        },
        sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
      },
      {
        title: t("Last Updated"),
        dataIndex: ["updatedAt"],
        key: "updatedAt",
        width: 100,
        render: (updatedAt: string) => {
          const d = formatDate(updatedAt)
          return (
            <span className="font-normal">
              <span className="text-black-600">{d.formattedTime}</span> {d.formattedDate}
            </span>
          )
        },
        sorter: (a, b) => a.updatedAt.localeCompare(b.updatedAt),
      },
      {
        title: t("Action"),
        key: "action",
        width: 100,
        render: (_, record) => (
          <Space size="middle" className="justify-center items-center">
            <Button
              type="primary"
              icon={<EditOutlined />}
              className="bg-blue-500 px-4 py-2 rounded"
              onClick={() => router.push(`/admin/upload/edit?id=${record.id}`)}
            >
              {t("Edit")}
            </Button>
            <Button danger type="primary" icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
              <span>{t("Delete")}</span>
            </Button>
          </Space>
        ),
      },
    ],
    [], // eslint-disable-line react-hooks/exhaustive-deps
  )

  /* ------------------------------- Handlers -------------------------------- */
  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: t("Confirm Delete"),
      content: t("Are you sure you want to delete this media file?"),
      okText: t("Delete"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          if (isView) {
            // xoá local ở table
            const after = table.data.filter(x => x.id !== id)
            // không nhất thiết gọi lại page — tuỳ bạn:
            await deleteMedia(id)
            message.success(t("Media file deleted successfully!"))
            // reload để sync total
            table.fetchPage({ ...table.pagination })
          } else {
            const after = grid.data.filter(x => x.id !== id)
            await deleteMedia(id)
            message.success(t("Media file deleted successfully!"))
            // reload nhẹ: reset và load page 1
            grid.loadMore(1)
          }
        } catch (error) {
          console.error("Handle Delete Error:", error)
          message.error(t("Failed to delete media file."))
        }
      },
    })
  }

  const showModal = (index: number) => setSelectedMedia(index)
  const handleCancel = () => setSelectedMedia(null)

  const handleFormSubmit = async (
    value: { title: string; alternativeText: string; caption: string; width?: number; height?: number },
    file?: File,
  ) => {
    try {
      const media = (dataItemForm || dataItem)[selectedMedia || 0] // ưu tiên form state nếu có
      const formData = new FormData()
      const data: FileUpdate = {
        fileInfo: { name: value.title, alternativeText: value.alternativeText, caption: value.caption },
        file,
      }
      formData.append("fileInfo", JSON.stringify(data.fileInfo))
      if (data.file) formData.append("files", data.file as Blob)

      await updateMedia(media.id, formData)
      // refresh theo mode
      if (isView) {
        await table.fetchPage({ ...table.pagination })
      } else {
        // reload lại từ đầu cho chắc
        await grid.loadMore(1)
      }
    } catch {
      message.error(t("Failed to update media information."))
    }
  }

  const handleSelectMultiMedia = (media: MediaData) => {
    const picked = selectedMediaMulti.some(m => m.id === media.id)
    if (picked) {
      setSelectedMediaMulti(prev => prev.filter(m => m.id !== media.id))
    } else {
      setSelectedMediaMulti(prev => [...prev, media])
    }
  }

  const handleSelectMedia = (media: MediaData) => {
    if (onSelectMedia) {
      if (selectMode === "multi") handleSelectMultiMedia(media)
      else onSelectMedia(media)
    }
    setSelectedMedia(null)
  }

  // Đồng bộ dataItem cho modal body (nếu component con trông chờ FormValuesMedia.dataItem)
  useEffect(() => {
    setValue("dataItem", dataItem)
  }, [dataItem, setValue])

  /* --------------------------------- Render -------------------------------- */
  return (
    <div
      style={{
        height: isOpenModal ? "650px" : "auto",
        overflowY: isOpenModal ? "scroll" : "visible",
        marginTop: 20,
        position: "relative",
      }}
      // Remount theo mode để không rò state ngầm giữa Table/Grid
      key={isView ? "table-mode" : "grid-mode"}
    >
      {/* Chế độ chọn nhiều (Grid) */}
      {selectMode === "multi" && !isView && (
        <div className="flex justify-end mb-4 sticky top-0 bg-white z-10">
          <Button
            type="primary"
            disabled={selectedMediaMulti.length === 0}
            onClick={() => onSelectMedia?.(selectedMediaMulti)}
          >
            {t("Select")} {selectedMediaMulti.length ? `${selectedMediaMulti.length} ${t("image")}` : ""}
          </Button>
        </div>
      )}

      {isView ? (
        /* ------------------------------- TABLE ------------------------------- */
        <Table
          className="mt-3 border border-gray-300 custom-table overflow-hidden"
          rowKey="id"
          columns={columns}
          loading={loading}
          dataSource={table.data}
          onChange={
            (pg => {
              const next = {
                current: pg.current || 1,
                pageSize: pg.pageSize || table.pagination.pageSize,
                total: table.pagination.total,
              }
              table.fetchPage(next)
            }) as TableProps<MediaData>["onChange"]
          }
          pagination={{
            current: table.pagination.current,
            pageSize: table.pagination.pageSize,
            total: table.pagination.total,
          }}
          scroll={{ x: "100%", y: 600 }}
          style={{ border: "1px solid #e8e8e8" }}
          rowClassName={(_, index) => (index % 2 === 0 ? "bg-[#f6f7f7]" : "bg-white")}
        />
      ) : (
        /* ------------------------------- GRID -------------------------------- */
        <>
          <div className="grid grid-cols-8 gap-4">
            {grid.data.map((media, index) => {
              const isPicked = selectedMediaMulti.some(m => m.id === media.id)
              return (
                <div
                  key={media.id}
                  className={`border rounded-lg transition-all duration-100 overflow-hidden ${
                    isPicked ? "border-blue-500" : "border-gray-200"
                  }`}
                >
                  {media.mime && media.mime.startsWith("video") ? (
                    <div
                      className="w-full aspect-square flex items-center justify-center bg-black cursor-pointer"
                      onClick={() => {
                        if (isOpenModal) {
                          if (selectMode === "multi") handleSelectMultiMedia(media)
                          else handleSelectMedia(media)
                        } else {
                          setSelectedMedia(index)
                        }
                      }}
                    >
                      <video
                        src={`${ADMIN_API_CONFIG.API_URL}${media.url}`}
                        controls={false}
                        className="w-full h-full object-cover"
                        style={{ pointerEvents: "none" }}
                      />
                    </div>
                  ) : (
                    <div
                      className="w-full aspect-square bg-center bg-no-repeat bg-cover transition-all duration-100 cursor-pointer"
                      style={{
                        backgroundImage: `url(${ADMIN_API_CONFIG.API_URL}${
                          media?.formats?.thumbnail?.url || media.url
                        })`,
                      }}
                      onClick={() => {
                        if (isOpenModal) {
                          if (selectMode === "multi") handleSelectMultiMedia(media)
                          else handleSelectMedia(media)
                        } else {
                          setSelectedMedia(index)
                        }
                      }}
                    />
                  )}

                  <Modal
                    open={selectedMedia === index}
                    style={{ top: 65 }}
                    width="95%"
                    closable={false}
                    footer={null}
                    title={
                      <div className="flex justify-between text-center">
                        <Title level={2} className="m-0">
                          {t("Attachment details")}
                        </Title>
                        <Space>
                          <Button type="text" onClick={() => setSelectedMedia(prev => (prev || 0) - 1)}>
                            <LeftOutlined />
                          </Button>
                          <Button type="text" onClick={() => setSelectedMedia(prev => (prev || 0) + 1)}>
                            <RightOutlined />
                          </Button>
                          <Button type="text" onClick={handleCancel} className="close-icon">
                            <CloseOutlined />
                          </Button>
                        </Space>
                      </div>
                    }
                  >
                    <MediaModalBodyEdit
                      media={media}
                      loading={loading}
                      handleFormSubmit={handleFormSubmit}
                      // refresh grid
                      refreshMediaList={() => grid.loadMore(1)}
                      setIsView={value => (isView = value)}
                    />
                  </Modal>
                </div>
              )
            })}
          </div>

          {grid.data.length < grid.total && (
            <div className="text-center my-4">
              <Button type="primary" onClick={() => grid.loadMore(grid.page + 1)} loading={grid.loading}>
                {t("See More")} ({grid.data.length}/{grid.total})
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default MediaList
